import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import ReactPaginate from "react-paginate";
import Swal from "sweetalert2";
import AsyncSelect from "react-select/async";
import getAxiosInstance from "./../../Utils/axios";
import { getCountries, getStates, getCities, getBuildings, getFloors, getIdList } from "./../../Utils/api";
import { DateTime } from "luxon";
import DateSelector from "../../Components/DateSelector";

const Toast = Swal.mixin({
    toast: true,
    position: "top-end",
    showConfirmButton: false,
    timer: 3000,
    timerProgressBar: true,
    didOpen: (toast) => {
        toast.addEventListener("mouseenter", Swal.stopTimer);
        toast.addEventListener("mouseleave", Swal.resumeTimer);
    },
});

export default function ScreenAvailable() {
    const [screens, setScreens] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [notAdmin, setNotAdmin] = useState(false);
    const [searchFilter, setSearchFilter] = useState("");
    const [limit, setLimit] = useState(20);
    const [pageNumber, setPageNumber] = useState(0);
    const [totalScreens, setTotalScreens] = useState(0);
    const [fromTime, setFromTime] = useState("");
    const [tillTime, setTillTime] = useState("");
    const [isClick, setIsClick] = useState(false);

    // Location
    const [country, setCountry] = useState([]);
    const [inputCountry, setInputCountry] = useState("");
    const [state, setState] = useState([]);
    const [inputState, setInputState] = useState("");
    const [city, setCity] = useState([]);
    const [inputCity, setInputCity] = useState("");
    const [building, setBuilding] = useState([]);
    const [inputBuilding, setInputBuilding] = useState("");
    const [floor, setFloor] = useState([]);
    const [inputFloor, setInputFloor] = useState("");

    const fetchCountryData = (inputValue) => getCountries(inputValue);
    const fetchStateData = (inputValue) => getStates(inputValue, getIdList(country));
    const fetchCityData = (inputValue) => getCities(inputValue, getIdList(country), getIdList(state));
    const fetchBuildingData = (inputValue) => getBuildings(inputValue, getIdList(country), getIdList(state), getIdList(city));
    const fetchFloorData = (inputValue) => getFloors(inputValue, getIdList(country), getIdList(state), getIdList(city), getIdList(building));

    const getScreens = (from, till) => {
        let jsonData = {
            limit: limit,
            page: pageNumber * limit,
            // search: searchFilter,
            country: getIdList(country),
            state: getIdList(state),
            city: getIdList(city),
            building: getIdList(building),
            floor: getIdList(floor),
            from: fromTime,
            till: tillTime,
        };
        // converting (json --> form-urlencoded)
        const data = Object.keys(jsonData)
            .map((key) => `${key}=${encodeURIComponent(jsonData[key])}`)
            .join("&");

        getAxiosInstance()
            .post("/admin/screens-availability", data, {
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
            })
            .then((res) => {
                // Validating form
                setIsLoading(false);
                if (res.data.status === "success") {
                    // console.log(res.data);
                    setTotalScreens(res.data.totalCount);
                    setScreens(res.data.result);
                } else if (res.data.status === "error") Toast.fire({ icon: "error", title: res.data.message });
                else Toast.fire({ icon: "error", title: "Cannot process request" });
            })
            .catch((err) => {
                setIsLoading(false);
                console.log(err);
            });
    };

    useEffect(() => {
        getScreens("", "");
    }, [limit, pageNumber, country, state, city, building, floor, isClick]);

    const changePage = ({ selected }) => setPageNumber(selected);

    useEffect(() => {
        getAxiosInstance()
            .get("/admin/me")
            .then((res) => {
                if (res.data.result.user_type !== "admin") {
                    setNotAdmin(true);
                    setCountry(res.data.result.location.country);
                    setState(res.data.result.location.state);
                    setCity(res.data.result.location.city);
                    setBuilding(res.data.result.location.building);
                    setFloor(res.data.result.location.floor);
                }
            })
            .catch((err) => console.log(err));
    }, []);

    return (
        <div>
            <div className="content-wrapper">
                <div className="content-header">
                    <div className="container-fluid">
                        <div className="row mb-3">
                            <div className="col-sm-6">
                                <h1 className="m-0">Screen Available Report</h1>
                            </div>
                        </div>
                        <div className="row mb-2">
                            <div className="col-md-2"></div>
                            <div className="col-md-2">
                                <AsyncSelect
                                    defaultOptions
                                    cacheOption
                                    isMulti
                                    placeholder="Countries"
                                    value={country}
                                    getOptionLabel={(e) => `${e.name} (${e.code})`}
                                    getOptionValue={(e) => e._id}
                                    loadOptions={fetchCountryData}
                                    onInputChange={setInputCountry}
                                    isDisabled={notAdmin}
                                    onChange={(e) => {
                                        setCountry(e);
                                        setState([]);
                                        setCity([]);
                                        setBuilding([]);
                                        setFloor([]);
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <AsyncSelect
                                    defaultOptions
                                    cacheOption
                                    isMulti
                                    placeholder="States"
                                    value={state}
                                    getOptionLabel={(e) => e.name}
                                    getOptionValue={(e) => e._id}
                                    loadOptions={fetchStateData}
                                    onInputChange={setInputState}
                                    isDisabled={notAdmin}
                                    onChange={(e) => {
                                        setState(e);
                                        setCity([]);
                                        setBuilding([]);
                                        setFloor([]);
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <AsyncSelect
                                    defaultOptions
                                    cacheOption
                                    isMulti
                                    placeholder="Cities"
                                    value={city}
                                    getOptionLabel={(e) => e.name}
                                    getOptionValue={(e) => e._id}
                                    loadOptions={fetchCityData}
                                    onInputChange={setInputCity}
                                    isDisabled={notAdmin}
                                    onChange={(e) => {
                                        setCity(e);
                                        setBuilding([]);
                                        setFloor([]);
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <AsyncSelect
                                    defaultOptions
                                    cacheOption
                                    isMulti
                                    placeholder="Buildings"
                                    value={building}
                                    getOptionLabel={(e) => e.name}
                                    getOptionValue={(e) => e._id}
                                    loadOptions={fetchBuildingData}
                                    onInputChange={setInputBuilding}
                                    isDisabled={notAdmin}
                                    onChange={(e) => {
                                        setBuilding(e);
                                        setFloor([]);
                                    }}
                                />
                            </div>
                            <div className="col-md-2">
                                <AsyncSelect defaultOptions cacheOption isMulti placeholder="Floors" value={floor} getOptionLabel={(e) => e.name} getOptionValue={(e) => e._id} loadOptions={fetchFloorData} onInputChange={setInputFloor} isDisabled={notAdmin} onChange={setFloor} />
                            </div>
                        </div>
                        <div className="row justify-content-center mt-3">
                            <div className="col-md-12">
                                <div className="card">
                                    <div className="card-header">
                                        <div class="card-tools">
                                            {/* <div className="input-group input-group-sm" style={{ width: 250 }}>
                                                <input type="datetime-local" name="table_search" className="form-control float-right" placeholder="Search" value={fromTime} onChange={(e) => setFromTime(e.target.value)} />
                                                <input type="datetime-local" name="table_search" className="form-control float-right" placeholder="Search" value={tillTime} onChange={(e) => setTillTime(e.target.value)} />
                                                <div className="input-group-append">
                                                    <button type="submit" className="btn btn-default" onClick={() => getScreens()}>
                                                        <i className="fas fa-search" />
                                                    </button>
                                                </div>
                                            </div> */}
                                            <DateSelector action={() => setIsClick(!isClick)} from={setFromTime} to={setTillTime} />
                                        </div>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-bordered">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: 20 }}>SL&nbsp;No. </th>
                                                    <th>Screen</th>
                                                    <th>Screen ID</th>
                                                    <th className="text-center">Time</th>
                                                    <th className="text-center">Status</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {isLoading && (
                                                    <tr style={{ textAlign: "center" }}>
                                                        <td colSpan={11}>Loading Screens ...</td>
                                                    </tr>
                                                )}
                                                {!isLoading && totalScreens === 0 && (
                                                    <tr style={{ textAlign: "center" }}>
                                                        <td colSpan={11}>No Screens Found</td>
                                                    </tr>
                                                )}
                                                {screens?.map((screen, i) => (
                                                    <tr key={i}>
                                                        <td>{i + 1}</td>
                                                        <td>{screen.screen?.name}</td>
                                                        <td>{screen.screen?.uid || screen.screen?.id}</td>
                                                        <td className="text-center">{DateTime.fromMillis(screen.timing).toFormat("dd LLL yyyy h:mm a")}</td>
                                                        <td className="text-center">{screen.status === 0 ? <span className="text-danger">Inactive</span> : <span className="text-success">Active</span>}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                    <div className="card-footer clearfix">
                                        {totalScreens > limit ? (
                                            <ReactPaginate
                                                previousLabel={"«"}
                                                nextLabel={"»"}
                                                pageCount={Math.ceil(totalScreens / limit)}
                                                onPageChange={changePage}
                                                containerClassName={"pagination pagination-sm m-0 float-right"}
                                                pageClassName={"page-item"}
                                                pageLinkClassName={"page-link"}
                                                // previousClassName={"previous"}
                                                previousLinkClassName={"page-link"}
                                                // nextClassName={"next"}
                                                nextLinkClassName={"page-link"}
                                                disabledClassName={"disabled"}
                                                activeClassName={"active"}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
